import React from 'react';
import {
  Shield, Activity, TrendingUp, BarChart3, Clock, Target,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const AssetPerformanceMonitoringPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Asset Performance Monitoring',
      title: (
        <>
          Monitor and assess <em>your entire renewable portfolio</em>
        </>
      ),
      lede: 'Monitor and assess the health of renewable asset portfolios in one view, highlight which assets need technical attention with our comprehensive performance monitoring solution.',
      image: '/assets/img/general/steptodown.com571771.jpg',
      imageAlt: 'Renewable energy assets under performance monitoring',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '99.9%', label: 'Uptime monitoring' },
        { value: '24/7', label: 'Real-time alerts' },
        { value: '500+', label: 'Assets monitored' },
        { value: '<1min', label: 'Response time' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Comprehensive capabilities',
      lede: 'Our asset performance monitoring platform provides end-to-end visibility and control over your renewable energy portfolio.',
      body: [
        'Get a complete picture of your renewable energy portfolio performance in one centralized dashboard. Identify issues quickly and take action before they impact operations.',
      ],
      checklist: [
        'Monitor and assess the health of renewable asset portfolios in one view',
        'Highlight which assets need technical attention',
        'Real-time performance monitoring across all renewable assets',
        'Advanced analytics and reporting dashboards',
        'Automated alert systems for maintenance and performance issues',
        'Predictive maintenance scheduling and optimization',
        'Comprehensive health assessment and diagnostics',
        'Integration with existing SCADA and monitoring systems',
      ],
      image: '/assets/img/general/solar-energy-7882650_1280.jpg',
      imageAlt: 'Solar assets reporting into a monitoring platform',
      imageCaption: 'One centralized view across every asset in the portfolio',
    }}
    capabilities={{
      eyebrow: 'Monitoring solutions',
      title: 'Comprehensive monitoring solutions',
      lede: 'Advanced monitoring capabilities to ensure optimal performance of your renewable energy assets.',
      items: [
        {
          icon: Activity,
          title: 'Real-time monitoring',
          description: 'Continuous monitoring of asset performance with instant alerts and notifications.',
        },
        {
          icon: BarChart3,
          title: 'Advanced analytics',
          description: 'Comprehensive data analysis with predictive insights and trend identification.',
        },
        {
          icon: Shield,
          title: 'Risk assessment',
          description: 'Proactive identification of potential issues before they impact performance.',
        },
        {
          icon: TrendingUp,
          title: 'Performance optimization',
          description: 'AI-driven recommendations to maximize efficiency and output.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Key benefits',
      title: 'Transform your renewable energy operations',
      lede: 'Comprehensive asset performance monitoring, applied across the portfolio.',
      items: [
        {
          icon: TrendingUp,
          title: 'Increased efficiency',
          description: 'Optimize energy generation and reduce operational costs through advanced monitoring.',
        },
        {
          icon: Shield,
          title: 'Enhanced reliability',
          description: 'Minimize downtime and ensure consistent performance with predictive maintenance.',
        },
        {
          icon: Clock,
          title: '24/7 surveillance',
          description: 'Round-the-clock monitoring with automated alerts for immediate response.',
        },
        {
          icon: Target,
          title: 'Proactive maintenance',
          description: 'Identify issues before they become problems with predictive analytics.',
        },
      ],
    }}
    cta={{
      title: 'Ready to see your whole portfolio in one view?',
      body: 'Talk to our team about connecting your assets to a single monitoring platform.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default AssetPerformanceMonitoringPage;
